import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Search, SlidersHorizontal, ArrowUpDown, X, Calculator, ArrowRightLeft, MapPin } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
// PageContainer not used in current layout
// import PageContainer from '@/components/layout/PageContainer'
import Card from '@/components/ui/Card'
import Skeleton from '@/components/ui/Skeleton'
import Spinner from '@/components/ui/Spinner'
import FacilityCard from '@/components/facility/FacilityCard'
import FacilityMapView from '@/components/facility/FacilityMapView'
import FacilityFilter, { type FilterState } from '@/components/facility/FacilityFilter'
import LocationBar from '@/components/facility/LocationBar'
import AddressSearchModal from '@/components/facility/AddressSearchModal'
import EmptyState from '@/components/common/EmptyState'
import EmptySearch from '@/components/illustrations/EmptySearch'
import { useFacilities, useBookmarkIds, useToggleBookmark } from '@/features/facility/api'
import type { LocationSearchResult } from '@/features/location/api'
import type { FacilitySearchFilter, FacilitySummary } from '@/types/facility'
import { FACILITY_TYPE_LABELS } from '@/types/common'
import type { FacilityType } from '@/types/common'
import apiClient from '@/lib/api/client'
import { cn } from '@/lib/utils'
import SEO from '@/components/common/SEO'
import { useIsDesktop } from '@/hooks/useMediaQuery'
import { trackSearchFacility } from '@/lib/analytics'
import PromotionSlot from '@/components/common/PromotionSlot'

/** 검색 리스트에 슬롯이 삽입될 위치 (4번째/12번째 카드 직후, 0-based index) */
const SEARCH_SLOT_INDEX_AFTER = 3
const SEARCH_SLOT_2_INDEX_AFTER = 11

/** 정렬 옵션 */
const SORT_OPTIONS = [
  { value: undefined, label: '추천순' },
  { value: 'DISTANCE' as const, label: '거리순' },
  { value: 'RATING' as const, label: '평가순' },
  { value: 'COST_LOW' as const, label: '비용 낮은순' },
  { value: 'COST_HIGH' as const, label: '비용 높은순' },
  { value: 'REVIEW_COUNT' as const, label: '리뷰순' },
] as const

/** 위치 상태 */
interface LocationState {
  latitude: number | null
  longitude: number | null
  name: string | null
  isGps: boolean
}

/** 스켈레톤 카드 */
function SkeletonCard() {
  return (
    <Card>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Skeleton width="8rem" height="1.25rem" />
            <Skeleton width="3rem" height="1.25rem" />
          </div>
          <div className="mt-2 flex gap-2">
            <Skeleton width="4rem" height="1rem" />
            <Skeleton width="8rem" height="1rem" />
          </div>
        </div>
        <Skeleton width="2rem" height="2rem" rounded />
      </div>
      <div className="mt-3 flex justify-between">
        <Skeleton width="8rem" height="1rem" />
        <Skeleton width="5rem" height="1rem" />
      </div>
    </Card>
  )
}

/**
 * GPS 현재 위치를 비동기로 가져온다.
 */
function getCurrentPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation API가 지원되지 않습니다'))
      return
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 300000, // 5분 캐시
    })
  })
}

/**
 * 좌표를 역지오코딩하여 주소명을 반환한다.
 */
async function reverseGeocode(lat: number, lng: number): Promise<string> {
  try {
    const response = await fetch(
      `/api/v1/location/reverse-geocode?latitude=${lat}&longitude=${lng}`
    )
    const data = await response.json()
    if (data.success && data.data && data.data.addressName) {
      return data.data.addressName
    }
  } catch {
    // 역지오코딩 실패 시 좌표를 간략 표시
  }
  return `${lat.toFixed(4)}, ${lng.toFixed(4)}`
}

/**
 * 시설 검색 페이지 v2.0
 *
 * - 검색바: 더 큰 라운드 + 그림자 + 아이콘 배치
 * - 필터 칩: 선택 시 컬러 + 그림자 변화
 * - 시설 카드: 정보 밀도 개선
 * - 스태거드 리스트 애니메이션
 */

/** 시도별 중심 좌표 */
const SIDO_CENTER: Record<string, { lat: number; lng: number }> = {
  '서울특별시': { lat: 37.5665, lng: 126.9780 },
  '경기도': { lat: 37.4138, lng: 127.5183 },
  '인천광역시': { lat: 37.4563, lng: 126.7052 },
  '강원특별자치도': { lat: 37.8228, lng: 128.1555 },
  '충청북도': { lat: 36.6357, lng: 127.4912 },
  '충청남도': { lat: 36.5184, lng: 126.8000 },
  '대전광역시': { lat: 36.3504, lng: 127.3845 },
  '세종특별자치시': { lat: 36.4800, lng: 127.0000 },
  '전북특별자치도': { lat: 35.8203, lng: 127.1088 },
  '전라남도': { lat: 34.8679, lng: 126.9910 },
  '광주광역시': { lat: 35.1595, lng: 126.8526 },
  '경상북도': { lat: 36.4919, lng: 128.8889 },
  '대구광역시': { lat: 35.8714, lng: 128.6014 },
  '경상남도': { lat: 35.4606, lng: 128.2132 },
  '울산광역시': { lat: 35.5384, lng: 129.3114 },
  '부산광역시': { lat: 35.1796, lng: 129.0756 },
  '제주특별자치도': { lat: 33.4996, lng: 126.5312 },
}

export default function FacilitySearchPage() {
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const [searchParams, setSearchParams] = useSearchParams()
  const [keyword, setKeyword] = useState('')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isSortOpen, setIsSortOpen] = useState(false)
  const [isAddressSearchOpen, setIsAddressSearchOpen] = useState(false)
  const [isCompareMode, setIsCompareMode] = useState(false)
  const [compareIds, setCompareIds] = useState<number[]>([])
  const [mobileViewMode, setMobileViewMode] = useState<'list' | 'map'>('map')
  const [selectedFacility, setSelectedFacility] = useState<(FacilitySummary & { ts: number }) | null>(null)
  const [autocompleteResults, setAutocompleteResults] = useState<{id: number; name: string; facilityType: string; addressSido: string; distanceKm: number | null}[]>([])
  const [showAutocomplete, setShowAutocomplete] = useState(false)
  const autocompleteTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 비용 계산기에서 넘어온 안내 배너
  const [costBanner, setCostBanner] = useState<{
    copayAmount: number
    totalMin: number
    totalMax: number
    careGrade: string
    facilityType: string
  } | null>(null)

  // URL 쿼리 파라미터에서 초기 필터 설정 (비용 계산기 연동)
  const initialTypes: string[] = []
  const qFacilityType = searchParams.get('facilityType')
  if (qFacilityType) initialTypes.push(qFacilityType)

  const [filterState, setFilterState] = useState<FilterState>({
    types: initialTypes as FacilityType[],
    grades: [],
  })
  const [sortBy, setSortBy] = useState<FacilitySearchFilter['sortBy']>(undefined)
  const [radiusKm, setRadiusKm] = useState(5)

  // 위치 상태
  const [location, setLocation] = useState<LocationState>({
    latitude: null,
    longitude: null,
    name: null,
    isGps: false,
  })
  const [isLocationLoading, setIsLocationLoading] = useState(true)
  const gpsAttempted = useRef(false)
  const userChangedLocation = useRef(false)

  // URL 쿼리 파라미터 처리 (홈페이지 keyword, 비용 계산기 등)
  useEffect(() => {
    const qKeyword = searchParams.get('keyword')
    if (qKeyword) {
      setKeyword(qKeyword)
      setSearchKeyword(qKeyword)
    }

    const qSido = searchParams.get('sido')
    if (qSido) {
      setFilterState((prev) => ({ ...prev, region: qSido }))
    }

    const copay = searchParams.get('copayAmount')
    const totalMin = searchParams.get('totalMin')
    const totalMax = searchParams.get('totalMax')
    const careGrade = searchParams.get('careGrade')
    const facilityType = searchParams.get('facilityType')

    if (copay && totalMin && totalMax) {
      setCostBanner({
        copayAmount: Number(copay),
        totalMin: Number(totalMin),
        totalMax: Number(totalMax),
        careGrade: careGrade || '',
        facilityType: facilityType || '',
      })
    }

  }, [searchParams])

  // GPS 위치 초기화 (첫 접속 시 한 번만)
  useEffect(() => {
    if (gpsAttempted.current) return
    gpsAttempted.current = true

    async function initGps() {
      setIsLocationLoading(true)
      try {
        const position = await getCurrentPosition()
        // 사용자가 이미 위치를 변경했으면 GPS 결과 무시
        if (userChangedLocation.current) return
        const { latitude, longitude } = position.coords
        const name = await reverseGeocode(latitude, longitude)
        // 다시 한번 체크 (reverseGeocode 대기 중에 변경될 수 있음)
        if (userChangedLocation.current) return
        setLocation({
          latitude,
          longitude,
          name,
          isGps: true,
        })
        setSortBy('DISTANCE')
      } catch {
        if (userChangedLocation.current) return
        // GPS 실패 시 서울 시청역 기본 위치
        setLocation({
          latitude: 37.5666,
          longitude: 126.9784,
          name: '서울특별시 중구',
          isGps: false,
        })
        setSortBy('DISTANCE')
      } finally {
        setIsLocationLoading(false)
      }
    }

    initGps()
  }, [])

  // 무한 스크롤 관찰 대상
  const loadMoreRef = useRef<HTMLDivElement>(null)

  // 검색 필터 조합
  // 지역 필터가 있으면 위치 기반 검색 비활성화 (sido와 lat/lng 충돌 방지)
  const useLocationSearch = !filterState.region && location.latitude != null

  // 지도 중심: 지역 필터 → 해당 시도 좌표, 없으면 GPS 위치
  const sidoCenter = filterState.region ? SIDO_CENTER[filterState.region] : null
  const mapCenterLat = sidoCenter?.lat ?? location.latitude
  const mapCenterLng = sidoCenter?.lng ?? location.longitude
  const searchFilter: FacilitySearchFilter = {
    keyword: searchKeyword || undefined,
    region: filterState.region,
    type: filterState.types.length > 0 ? filterState.types : undefined,
    grade: filterState.grades.length > 0 ? filterState.grades : undefined,
    sortBy,
    latitude: useLocationSearch ? (location.latitude ?? undefined) : undefined,
    longitude: useLocationSearch ? (location.longitude ?? undefined) : undefined,
    radiusKm: useLocationSearch ? radiusKm : undefined,
    hasDementiaCare: filterState.hasDementiaCare || undefined,
    hasVacancy: filterState.hasVacancy || undefined,
    hasPrograms: filterState.hasPrograms || undefined,
  }

  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFacilities(searchFilter)

  // 북마크 상태
  const { data: bookmarkIds } = useBookmarkIds()
  const { toggle: toggleBookmark } = useToggleBookmark()

  // 무한 스크롤: IntersectionObserver
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0]
      if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      rootMargin: '200px',
    })
    const el = loadMoreRef.current
    if (el) observer.observe(el)
    return () => {
      if (el) observer.unobserve(el)
    }
  }, [handleObserver])

  // 검색 실행
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearchKeyword(keyword)
    trackSearchFacility(keyword, filterState.region, activeFilterCount)
  }

  // 필터 적용
  const handleFilterApply = useCallback((filter: FilterState) => {
    setFilterState(filter)
  }, [])

  // 정렬 변경
  const handleSortChange = useCallback((value: FacilitySearchFilter['sortBy']) => {
    setSortBy(value)
    setIsSortOpen(false)
  }, [])

  // 주소 선택 (카카오 주소 검색 결과)
  const handleAddressSelect = useCallback((result: LocationSearchResult) => {
    userChangedLocation.current = true
    setIsLocationLoading(false)
    setLocation({
      latitude: result.latitude,
      longitude: result.longitude,
      name: result.addressName,
      isGps: false,
    })
    setSortBy((prev) => (!prev || prev !== 'DISTANCE') ? 'DISTANCE' : prev)
  }, [])

  // GPS 현재 위치로 재설정
  const handleResetToGps = async () => {
    userChangedLocation.current = false
    setIsLocationLoading(true)
    try {
      const position = await getCurrentPosition()
      const { latitude, longitude } = position.coords
      const name = await reverseGeocode(latitude, longitude)
      setLocation({
        latitude,
        longitude,
        name,
        isGps: true,
      })
    } catch (err: any) {
      if (err?.code === 1) {
        // PERMISSION_DENIED
        alert('위치 권한이 차단되어 있습니다.\n\n브라우저 주소창 왼쪽의 자물쇠(🔒) 아이콘을 클릭하여\n위치 권한을 "허용"으로 변경한 후 다시 시도해주세요.')
      }
    } finally {
      setIsLocationLoading(false)
    }
  }

  // 반경 변경
  const handleRadiusChange = useCallback((radius: number) => {
    setRadiusKm(radius)
  }, [])

  // 비교 토글
  const handleCompareToggle = useCallback((id: number) => {
    setCompareIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 3 ? [...prev, id] : prev
    )
  }, [])

  // 전체 결과 수
  const totalElements = data?.pages[0]?.data?.totalElements ?? data?.pages[0]?.meta?.totalElements ?? 0

  // 모든 페이지의 시설 리스트 합치기 (필터는 백엔드에서 처리)
  const facilities = useMemo(
    () => data?.pages.flatMap((page) => page.data?.content ?? []) ?? [],
    [data?.pages]
  )

  // 활성 필터 수
  const activeFilterCount = useMemo(
    () =>
      filterState.types.length +
      filterState.grades.length +
      (filterState.hasDementiaCare ? 1 : 0) +
      (filterState.hasVacancy ? 1 : 0) +
      (filterState.hasPrograms ? 1 : 0),
    [filterState.types.length, filterState.grades.length, filterState.hasDementiaCare, filterState.hasVacancy, filterState.hasPrograms]
  )

  // 위치 기반 검색 중인지
  const isLocationSearch = location.latitude != null && location.longitude != null

  // 정렬 옵션 (위치 없으면 거리순 비활성화)
  const availableSortOptions = useMemo(
    () => SORT_OPTIONS.filter(
      (option) => option.value !== 'DISTANCE' || isLocationSearch
    ),
    [isLocationSearch]
  )

  // ── 공통 JSX 조각 ──────────────────────────────────────────────

  /** 위치 바 */
  const locationBarJsx = (
    <LocationBar
      locationName={location.name}
      radiusKm={radiusKm}
      onChangeLocation={() => setIsAddressSearchOpen(true)}
      onResetToGps={handleResetToGps}
      onRadiusChange={handleRadiusChange}
      isGpsLocation={location.isGps}
      isLoading={isLocationLoading}
    />
  )

  /** 비용 계산기 연동 배너 */
  const costBannerJsx = (
    <AnimatePresence>
      {costBanner && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="overflow-hidden"
        >
          <div className="rounded-2xl border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-cb-primary-light mt-0.5">
                <Calculator size={18} className="text-cb-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-body-sm font-bold text-cb-gray-900">비용 계산 기준 검색</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  <span className="inline-flex items-center rounded-full bg-white border border-blue-200 px-2.5 py-0.5 text-[11px] font-semibold text-blue-700">
                    {costBanner.careGrade?.replace('GRADE_', '') || '?'}등급
                  </span>
                  <span className="inline-flex items-center rounded-full bg-white border border-blue-200 px-2.5 py-0.5 text-[11px] font-semibold text-blue-700">
                    {costBanner.facilityType === 'NURSING_HOME' ? '요양원' :
                     costBanner.facilityType === 'NURSING_HOSPITAL' ? '요양병원' :
                     costBanner.facilityType === 'DAY_CARE' ? '주간보호' :
                     costBanner.facilityType === 'HOME_CARE_CENTER' ? '재가복지센터' : '시설'}
                  </span>
                  <span className="inline-flex items-center rounded-full bg-cb-primary/10 border border-cb-primary/20 px-2.5 py-0.5 text-[11px] font-bold text-cb-primary">
                    월 약 {Math.round(costBanner.totalMin / 10000)}~{Math.round(costBanner.totalMax / 10000)}만원
                  </span>
                </div>
                <p className="mt-1.5 text-[10px] text-cb-gray-400">시설별 실제 비용은 상세 페이지의 비용 탭에서 확인하세요</p>
              </div>
              <button
                onClick={() => {
                  setCostBanner(null)
                  setSearchParams({})
                }}
                className="text-cb-gray-400 hover:text-cb-gray-600 p-1.5 rounded-full hover:bg-white/60 transition-all flex-shrink-0"
                aria-label="비용 안내 닫기"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )

  /** 검색바 */
  const searchBarJsx = (
    <form onSubmit={handleSearch} className="relative">
      <input
        type="search"
        value={keyword}
        onChange={(e) => {
          const val = e.target.value
          setKeyword(val)
          if (autocompleteTimer.current) clearTimeout(autocompleteTimer.current)
          if (val.trim().length >= 2) {
            autocompleteTimer.current = setTimeout(async () => {
              try {
                const acParams: Record<string, any> = { query: val.trim() }
                if (location.latitude) acParams.latitude = location.latitude
                if (location.longitude) acParams.longitude = location.longitude
                const res = await apiClient.get('/facilities/autocomplete', { params: acParams })
                setAutocompleteResults(res.data.data ?? [])
                setShowAutocomplete(true)
              } catch { setAutocompleteResults([]) }
            }, 300)
          } else {
            setAutocompleteResults([])
            setShowAutocomplete(false)
          }
        }}
        onFocus={() => { if (autocompleteResults.length > 0) setShowAutocomplete(true) }}
        onBlur={() => setTimeout(() => setShowAutocomplete(false), 200)}
        placeholder="시설명으로 검색"
        className={cn(
          'h-[3.25rem] w-full rounded-2xl border-2 border-cb-gray-200 bg-white pl-4 pr-14',
          'text-body text-cb-gray-900 placeholder:text-cb-gray-400',
          'focus:outline-none focus:ring-2 focus:ring-cb-primary/20 focus:border-cb-primary focus:shadow-[0_0_0_3px_rgba(76,110,245,0.1)]',
          'transition-all duration-200 shadow-sm'
        )}
        aria-label="시설 검색"
      />
      <button
        type="submit"
        className="absolute right-1.5 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-b from-cb-primary to-cb-primary-hover text-white shadow-md shadow-cb-primary/20 hover:brightness-110 transition-all duration-200"
        aria-label="검색"
      >
        <Search size={20} />
      </button>

      {/* 자동완성 드롭다운 */}
      {showAutocomplete && autocompleteResults.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1.5 rounded-2xl border border-cb-gray-100 bg-white shadow-[var(--shadow-float)] overflow-hidden">
          {autocompleteResults.map((item) => (
            <button
              key={item.id}
              type="button"
              onMouseDown={() => {
                navigate(`/facilities/${item.id}`)
                setShowAutocomplete(false)
              }}
              className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-cb-gray-50 transition-colors border-b border-cb-gray-50 last:border-0"
            >
              <div className="flex-1 min-w-0">
                <p className="text-body-sm font-medium text-cb-gray-900 truncate">{item.name}</p>
                <p className="text-caption text-cb-gray-400">
                  {FACILITY_TYPE_LABELS[item.facilityType] ?? item.facilityType} · {item.addressSido}
                  {item.distanceKm != null && (
                    <span className="ml-1 text-cb-primary font-medium">
                      {item.distanceKm < 1 ? `${Math.round(item.distanceKm * 1000)}m` : `${item.distanceKm}km`}
                    </span>
                  )}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </form>
  )

  /** 필터 + 정렬 바 */
  const filterSortBarJsx = (
    <div className="flex items-center justify-between">
      <button
        onClick={() => setIsFilterOpen(true)}
        className={cn(
          'flex items-center gap-1.5 rounded-xl border-2 px-3.5 py-2 text-body-sm transition-all duration-200',
          activeFilterCount > 0
            ? 'border-cb-primary bg-cb-primary-light text-cb-primary font-semibold shadow-sm shadow-cb-primary/10'
            : 'border-cb-gray-200 text-cb-gray-600 hover:border-cb-gray-300 hover:bg-cb-gray-50'
        )}
        aria-label={`필터 ${activeFilterCount > 0 ? `(${activeFilterCount}개 적용됨)` : ''}`}
      >
        <SlidersHorizontal size={16} />
        필터
        {activeFilterCount > 0 && (
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-cb-primary text-[10px] font-bold text-white">
            {activeFilterCount}
          </span>
        )}
      </button>

      {/* 정렬 */}
      <div className="relative">
        <button
          onClick={() => setIsSortOpen(!isSortOpen)}
          className="flex items-center gap-1.5 text-body-sm font-medium text-cb-gray-600 hover:text-cb-gray-900 transition-colors"
          aria-label="정렬 변경"
          aria-expanded={isSortOpen}
        >
          <ArrowUpDown size={14} />
          {availableSortOptions.find((o) => o.value === sortBy)?.label ?? '추천순'}
        </button>

        {/* 정렬 드롭다운 */}
        <AnimatePresence>
          {isSortOpen && (
            <>
              <div
                className="fixed inset-0 z-30"
                onClick={() => setIsSortOpen(false)}
                aria-hidden="true"
              />
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
                className="absolute right-0 top-full z-40 mt-1.5 w-40 rounded-2xl border border-cb-gray-100 bg-white shadow-[var(--shadow-float)]"
              >
                {availableSortOptions.map((option) => (
                  <button
                    key={option.label}
                    onClick={() => handleSortChange(option.value)}
                    className={cn(
                      'w-full px-4 py-2.5 text-left text-body-sm transition-colors',
                      'first:rounded-t-2xl last:rounded-b-2xl',
                      sortBy === option.value
                        ? 'bg-cb-primary-light text-cb-primary font-semibold'
                        : 'text-cb-gray-700 hover:bg-cb-gray-50'
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  )

  /** 적용된 필터 태그 */
  const filterTagsJsx = activeFilterCount > 0 ? (
    <div className="flex flex-wrap gap-1.5">
      {filterState.types.map((type) => (
        <button
          key={type}
          onClick={() => setFilterState({ ...filterState, types: filterState.types.filter((t) => t !== type) })}
          className="inline-flex items-center gap-1 rounded-full bg-cb-primary-light px-2.5 py-1 text-caption font-medium text-cb-primary"
        >
          {FACILITY_TYPE_LABELS[type as keyof typeof FACILITY_TYPE_LABELS] ?? type}
          <X size={12} />
        </button>
      ))}
      {filterState.grades.map((grade) => (
        <button
          key={grade}
          onClick={() => setFilterState({ ...filterState, grades: filterState.grades.filter((g) => g !== grade) })}
          className="inline-flex items-center gap-1 rounded-full bg-cb-primary-light px-2.5 py-1 text-caption font-medium text-cb-primary"
        >
          {grade}등급
          <X size={12} />
        </button>
      ))}
      {filterState.hasDementiaCare && (
        <button
          onClick={() => setFilterState({ ...filterState, hasDementiaCare: false })}
          className="inline-flex items-center gap-1 rounded-full bg-cb-secondary-light px-2.5 py-1 text-caption font-medium text-cb-secondary"
        >
          치매전담
          <X size={12} />
        </button>
      )}
      {filterState.hasVacancy && (
        <button
          onClick={() => setFilterState({ ...filterState, hasVacancy: false })}
          className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-caption font-medium text-green-700"
        >
          여유정원
          <X size={12} />
        </button>
      )}
      {filterState.hasPrograms && (
        <button
          onClick={() => setFilterState({ ...filterState, hasPrograms: false })}
          className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-caption font-medium text-amber-700"
        >
          프로그램
          <X size={12} />
        </button>
      )}
    </div>
  ) : null

  /** 결과 수 + 비교 모드 */
  const resultsCountJsx = !isLoading && !isError ? (
    <div className="flex items-center justify-between">
      <p className="text-body-sm text-cb-gray-500">
        {searchKeyword ? (
          // 키워드 검색은 반경 제한 없이 전국에서 시설명·지역명으로 찾는다.
          <>전국 <span className="font-bold text-cb-gray-900">'{searchKeyword}'</span> 검색 결과 <span className="font-bold text-cb-gray-900 text-amount">{totalElements.toLocaleString()}</span>개</>
        ) : isLocationSearch ? (
          <>반경 {radiusKm}km 내 <span className="font-bold text-cb-gray-900 text-amount">{totalElements.toLocaleString()}</span>개 시설</>
        ) : (
          <>검색 결과 <span className="font-bold text-cb-gray-900 text-amount">{totalElements.toLocaleString()}</span>개</>
        )}
      </p>

      <button
        onClick={() => { setIsCompareMode(!isCompareMode); setCompareIds([]) }}
        className={cn(
          'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-caption font-semibold transition-all',
          isCompareMode
            ? 'bg-cb-primary text-white'
            : 'bg-cb-gray-50 text-cb-gray-600 hover:bg-cb-gray-100'
        )}
      >
        <ArrowRightLeft size={13} />
        {isCompareMode ? `비교 ${compareIds.length}/3` : '비교하기'}
      </button>
    </div>
  ) : null

  /** 비교 모드 안내 */
  const compareBannerJsx = isCompareMode ? (
    <div className="rounded-xl bg-cb-primary-light px-4 py-2.5 text-center text-caption font-medium text-cb-primary">
      비교할 시설을 2~3개 선택해주세요
    </div>
  ) : null

  /** 로딩 상태 (첫 페이지) */
  const loadingJsx = isLoading ? (
    <div className="flex flex-col gap-3">
      <SkeletonCard />
      <SkeletonCard />
      <SkeletonCard />
      <SkeletonCard />
    </div>
  ) : null

  /** 에러 상태 */
  const errorJsx = isError ? (
    <EmptyState
      message="시설 목록을 불러오지 못했어요"
      description={
        error instanceof Error
          ? error.message
          : '잠시 후 다시 시도해주세요.'
      }
      actionLabel="다시 시도"
      onAction={() => window.location.reload()}
    />
  ) : null

  /** 빈 결과 */
  const emptyJsx = !isLoading && !isError && facilities.length === 0 ? (
    <EmptyState
      illustration={EmptySearch}
      message={
        isLocationSearch
          ? `반경 ${radiusKm}km 내 시설을 찾지 못했어요`
          : '조건에 맞는 시설을 찾지 못했어요'
      }
      description={
        isLocationSearch
          ? '반경을 넓히거나 다른 위치로 검색해보세요'
          : '검색 범위를 넓혀볼까요?'
      }
      actionLabel={isLocationSearch ? '반경 넓히기' : '필터 초기화'}
      onAction={() => {
        if (isLocationSearch) {
          const radiusSteps = [3, 5, 10, 20]
          const currentIndex = radiusSteps.indexOf(radiusKm)
          if (currentIndex < radiusSteps.length - 1) {
            setRadiusKm(radiusSteps[currentIndex + 1] ?? radiusKm)
          } else {
            setFilterState({ types: [], grades: [] })
            setSearchKeyword('')
            setKeyword('')
          }
        } else {
          setFilterState({ types: [], grades: [] })
          setSearchKeyword('')
          setKeyword('')
          setSortBy(undefined)
        }
      }}
    />
  ) : null

  /** 시설 카드 리스트 */
  const facilityListJsx = (clickToSelect: boolean) =>
    facilities.length > 0 ? (
      <div className="flex flex-col gap-3" role="list" aria-label="시설 검색 결과">
        {facilities.map((facility, index) => (
          <div key={facility.id}>
            <motion.div
              role="listitem"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(index * 0.04, 0.3), duration: 0.3 }}
              className={clickToSelect && selectedFacility?.id === facility.id ? 'ring-2 ring-cb-primary rounded-2xl' : ''}
            >
              <FacilityCard
                facility={facility}
                isBookmarked={bookmarkIds?.has(facility.id)}
                onToggleBookmark={toggleBookmark}
                isCompareMode={isCompareMode}
                isCompareSelected={compareIds.includes(facility.id)}
                onCompareToggle={handleCompareToggle}
                onCardClick={clickToSelect ? (id) => {
                  const f = facilities.find(fac => fac.id === id)
                  if (f && f.latitude && f.longitude) {
                    setSelectedFacility({ ...f, ts: Date.now() })
                  }
                } : undefined}
              />
            </motion.div>
            {/* 4번째 카드 직후 프로모션 슬롯 1회 노출 */}
            {index === SEARCH_SLOT_INDEX_AFTER && (
              <PromotionSlot slotKey="search_list_4" className="mt-3" />
            )}
            {/* 12번째 카드 직후 프로모션 슬롯 1회 노출 */}
            {index === SEARCH_SLOT_2_INDEX_AFTER && (
              <PromotionSlot slotKey="search_list_12" className="mt-3" />
            )}
          </div>
        ))}
      </div>
    ) : null

  /** 무한 스크롤 트리거 */
  const loadMoreJsx = (
    <div ref={loadMoreRef} className="flex justify-center py-4">
      {isFetchingNextPage && <Spinner size="md" />}
      {!hasNextPage && facilities.length > 0 && (
        <p className="text-body-sm text-cb-gray-400">
          모든 검색 결과를 확인했어요
        </p>
      )}
    </div>
  )

  /** 비교 CTA (고정 — 하단) */
  const compareCta = isCompareMode && compareIds.length >= 2 ? (
    <div className={cn(
      'fixed left-0 right-0 z-50 px-[var(--spacing-page-x)] pb-2',
      isDesktop ? 'bottom-4' : 'bottom-[calc(4rem+env(safe-area-inset-bottom))]'
    )}>
      <div className={cn('mx-auto', isDesktop ? 'max-w-[420px]' : 'max-w-[480px]')}>
        <button
          onClick={() => {
            setIsCompareMode(false)
            navigate(`/compare?ids=${compareIds.join(',')}`)
          }}
          className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-cb-primary text-white text-body font-bold shadow-xl shadow-cb-primary/40 active:scale-[0.98] transition-transform"
        >
          <ArrowRightLeft size={20} />
          {compareIds.length}개 시설 비교하기
        </button>
      </div>
    </div>
  ) : null

  /** 공통 모달 */
  const modalsJsx = (
    <>
      <FacilityFilter
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        currentFilter={filterState}
        onApply={handleFilterApply}
      />
      <AddressSearchModal
        isOpen={isAddressSearchOpen}
        onClose={() => setIsAddressSearchOpen(false)}
        onSelect={handleAddressSelect}
      />
    </>
  )

  // ── 데스크톱 레이아웃: 좌측 리스트 + 우측 지도 ───────────────
  if (isDesktop) {
    return (
      <>
        <SEO title="내 주변 요양시설 검색" description="위치 기반으로 주변 요양시설을 검색하고 비교하세요. 평가등급, 비용, 프로그램 정보를 한눈에 확인할 수 있습니다." url="/facilities" />
        <div className="fixed inset-0 top-16 flex overflow-hidden bg-white z-10">
          {/* 좌측 패널: 검색 + 리스트 */}
          <div className="w-[420px] flex-shrink-0 border-r border-cb-gray-200 flex flex-col overflow-hidden">
            {/* 검색 컨트롤 영역 */}
            <div className="p-4 space-y-3 border-b border-cb-gray-100 flex-shrink-0 relative z-20">
              {/* 지역 필터 활성 시 지역명 배지 표시, 아니면 위치 바 */}
              {filterState.region ? (
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-cb-primary/10 px-3 py-1.5 text-sm font-semibold text-cb-primary">
                    <MapPin size={14} /> {filterState.region}
                  </span>
                  <button
                    onClick={() => setFilterState((prev) => ({ ...prev, region: undefined }))}
                    className="text-xs text-gray-400 hover:text-gray-600"
                  >
                    초기화
                  </button>
                </div>
              ) : (
                locationBarJsx
              )}
              {costBannerJsx}
              {searchBarJsx}

              {filterSortBarJsx}
              {filterTagsJsx}
            </div>

            {/* 결과 수 */}
            <div className="px-4 py-2 border-b border-cb-gray-100">
              {resultsCountJsx}
              {compareBannerJsx}
            </div>

            {/* 스크롤 가능한 시설 리스트 */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {loadingJsx}
              {errorJsx}
              {emptyJsx}
              {facilityListJsx(true)}
              {loadMoreJsx}
            </div>
          </div>

          {/* 우측 패널: 지도 */}
          <div className="flex-1 h-full">
            {mapCenterLat != null && mapCenterLng != null ? (
              <FacilityMapView
                filter={searchFilter}
                centerLat={mapCenterLat}
                centerLng={mapCenterLng}
                selectedFacility={selectedFacility}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-cb-gray-400">
                위치를 설정하면 지도에 시설이 표시됩니다
              </div>
            )}
          </div>

          {/* 우측 세로배너 컬럼 160x600 — xl 이상에서 지도 옆에 전용 여백+배너 */}
          <div className="hidden xl:flex flex-shrink-0 w-[176px] items-start justify-center border-l border-cb-gray-200 bg-cb-gray-50 pt-6">
            <PromotionSlot slotKey="facility_search_side_right" />
          </div>
        </div>

        {compareCta}
        {modalsJsx}
      </>
    )
  }

  // ── 모바일 레이아웃: 탭 전환 (리스트 / 지도) ──────────────────

  return (
    <div className="flex flex-col h-[calc(100dvh-env(safe-area-inset-bottom)-4rem-3.25rem)]">
      <SEO title="내 주변 요양시설 검색" description="위치 기반으로 주변 요양시설을 검색하고 비교하세요. 평가등급, 비용, 프로그램 정보를 한눈에 확인할 수 있습니다." url="/facilities" />

      {/* 상단 고정: 검색 컨트롤 */}
      <div className="flex-shrink-0 bg-white border-b border-gray-100 px-4 pt-3 pb-2 space-y-2">
        {/* 지역 필터 or 위치 바 (컴팩트) */}
        {filterState.region ? (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-cb-primary/10 px-3 py-1 text-xs font-semibold text-cb-primary">
              <MapPin size={12} /> {filterState.region}
            </span>
            <button onClick={() => setFilterState((prev) => ({ ...prev, region: undefined }))} className="text-[11px] text-gray-400">초기화</button>
          </div>
        ) : (
          locationBarJsx
        )}
        {searchBarJsx}
        {filterSortBarJsx}
        {filterTagsJsx}

        {/* 탭 전환 */}
        <div className="flex rounded-lg bg-gray-100 p-0.5">
          <button
            onClick={() => setMobileViewMode('map')}
            className={`flex-1 rounded-md py-2 text-xs font-semibold transition-colors ${mobileViewMode === 'map' ? 'bg-white text-cb-primary shadow-sm' : 'text-gray-500'}`}
          >
            지도
          </button>
          <button
            onClick={() => setMobileViewMode('list')}
            className={`flex-1 rounded-md py-2 text-xs font-semibold transition-colors ${mobileViewMode === 'list' ? 'bg-white text-cb-primary shadow-sm' : 'text-gray-500'}`}
          >
            리스트
          </button>
        </div>
      </div>

      {/* 콘텐츠 영역 */}
      <div className="flex-1 overflow-hidden">
        {mobileViewMode === 'list' ? (
          <div className="h-full overflow-y-auto px-4 py-3 space-y-3">
            {resultsCountJsx}
            {compareBannerJsx}
            {loadingJsx}
            {errorJsx}
            {emptyJsx}
            {facilityListJsx(false)}
            {loadMoreJsx}
          </div>
        ) : (
          <div className="h-full">
            {mapCenterLat != null && mapCenterLng != null ? (
              <FacilityMapView
                filter={searchFilter}
                centerLat={mapCenterLat}
                centerLng={mapCenterLng}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-gray-400">
                위치를 설정하면 지도가 표시됩니다
              </div>
            )}
          </div>
        )}
      </div>

      {compareCta}
      {modalsJsx}
    </div>
  )
}
